import React, { useState, useEffect } from 'react';
import { Package, DollarSign, Hash, FileText, Building, Scale, Plus, AlertCircle, CheckCircle } from 'lucide-react';
import { createProduct } from '../../../../services/apiProduct';
import { getAllSuppliers } from '../../../../services/apiSupplier';

const CreateProductForm = () => {
    const [formData, setFormData] = useState({
        name: '',
        sku: '',
        description: '',
        supplier: '',
        unitOfMeasure: 'pieces',
        costPrice: '',
        sellingPrice: '',
        currentStock: '0',
        isActive: true
    });

    const [suppliers, setSuppliers] = useState([{}]);

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [errors, setErrors] = useState({});

    const unitOptions = ['pieces', 'kg', 'grams', 'liters', 'meters', 'boxes'];


      useEffect(() => {
        const fetchingSupplier = async()=>{
            const res = await getAllSuppliers()
            console.log(res.data)
            setSuppliers(res.data)
        }
        fetchingSupplier()
      }, []);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Product name is required';
        }

        if (!formData.sku.trim()) {
            newErrors.sku = 'SKU is required';
        } else if (formData.sku.length < 3) {
            newErrors.sku = 'SKU must be at least 3 characters';
        }

        if (!formData.supplier) {
            newErrors.supplier = 'Supplier is required';
        }

        if (!formData.costPrice || parseFloat(formData.costPrice) < 0) {
            newErrors.costPrice = 'Valid cost price is required';
        }

        if (!formData.sellingPrice || parseFloat(formData.sellingPrice) < 0) {
            newErrors.sellingPrice = 'Valid selling price is required';
        }

        if (parseFloat(formData.currentStock) < 0) {
            newErrors.currentStock = 'Stock cannot be negative';
        }

        if (parseFloat(formData.sellingPrice) < parseFloat(formData.costPrice)) {
            newErrors.sellingPrice = 'Selling price should be higher than cost price';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear specific error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const generateSKU = () => {
        const timestamp = Date.now().toString().slice(-6);
        const random = Math.random().toString(36).substring(2, 5).toUpperCase();
        const generatedSKU = `PRD-${timestamp}-${random}`;

        setFormData(prev => ({
            ...prev,
            sku: generatedSKU
        }));
    };

    const handleSubmit = async () => {

        if (!validateForm()) {
            setMessage({ type: 'error', text: 'Please fix the errors below' });
            return;
        }

        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const productData = {
                ...formData,
                costPrice: parseFloat(formData.costPrice),
                sellingPrice: parseFloat(formData.sellingPrice),
                currentStock: parseInt(formData.currentStock),
                sku: formData.sku.toUpperCase()
            };

            const result = await createProduct(productData);
            console.log(result, "Product Added")



            setMessage({ type: 'success', text: 'Product created successfully!' });

            // Reset form
            setFormData({
                name: '',
                sku: '',
                description: '',
                supplier: '',
                unitOfMeasure: 'pieces',
                costPrice: '',
                sellingPrice: '',
                currentStock: '0',
                isActive: true
            });

        } catch (error) {
            setMessage({ type: 'error', text: error.message || 'Failed to create product' });
        } finally {
            setLoading(false);
        }
    };

    const calculateMargin = () => {
        const cost = parseFloat(formData.costPrice) || 0;
        const selling = parseFloat(formData.sellingPrice) || 0;

        if (cost > 0 && selling > 0) {
            const margin = ((selling - cost) / selling * 100).toFixed(1);
            return margin;
        }
        return '0';
    };

    return (
        <div className="max-w-4xl mx-auto p-6 bg-white">
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                    <Package className="w-8 h-8 text-blue-600" />
                    <h1 className="text-3xl font-bold text-gray-800">Create New Product</h1>
                </div>
                <p className="text-gray-600">Add a new product to your inventory</p>
            </div>

            {message.text && (
                <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${message.type === 'success'
                        ? 'bg-green-50 border border-green-200 text-green-800'
                        : 'bg-red-50 border border-red-200 text-red-800'
                    }`}>
                    {message.type === 'success' ?
                        <CheckCircle className="w-5 h-5" /> :
                        <AlertCircle className="w-5 h-5" />
                    }
                    {message.text}
                </div>
            )}

            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Product Name */}
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <Package className="w-4 h-4 inline mr-2" />
                            Product Name *
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-500' : 'border-gray-300'
                                }`}
                            placeholder="Enter product name"
                        />
                        {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                    </div>

                    {/* SKU */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <Hash className="w-4 h-4 inline mr-2" />
                            SKU *
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                name="sku"
                                value={formData.sku}
                                onChange={handleInputChange}
                                className={`flex-1 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.sku ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                placeholder="Product SKU"
                            />
                            <button
                                type="button"
                                onClick={generateSKU}
                                className="px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                title="Generate SKU"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                        {errors.sku && <p className="text-red-500 text-sm mt-1">{errors.sku}</p>}
                    </div>

                    {/* Supplier */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <Building className="w-4 h-4 inline mr-2" />
                            Supplier *
                        </label>
                        <select
                            name="supplier"
                            value={formData.supplier}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.supplier ? 'border-red-500' : 'border-gray-300'
                                }`}
                        >
                            <option value="">Select a supplier</option>
                            {suppliers.map(supplier => (
                                <option key={supplier._id} value={supplier._id}>
                                    {supplier.name}
                                </option>
                            ))}
                        </select>
                        {errors.supplier && <p className="text-red-500 text-sm mt-1">{errors.supplier}</p>}
                    </div>

                    {/* Unit of Measure */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <Scale className="w-4 h-4 inline mr-2" />
                            Unit of Measure
                        </label>
                        <select
                            name="unitOfMeasure"
                            value={formData.unitOfMeasure}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {unitOptions.map(unit => (
                                <option key={unit} value={unit}>
                                    {unit.charAt(0).toUpperCase() + unit.slice(1)}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Current Stock */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Current Stock
                        </label>
                        <input
                            type="number"
                            name="currentStock"
                            value={formData.currentStock}
                            onChange={handleInputChange}
                            min="0"
                            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.currentStock ? 'border-red-500' : 'border-gray-300'
                                }`}
                            placeholder="0"
                        />
                        {errors.currentStock && <p className="text-red-500 text-sm mt-1">{errors.currentStock}</p>}
                    </div>

                    {/* Cost Price */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <DollarSign className="w-4 h-4 inline mr-2" />
                            Cost Price *
                        </label>
                        <input
                            type="number"
                            name="costPrice"
                            value={formData.costPrice}
                            onChange={handleInputChange}
                            min="0"
                            step="0.01"
                            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.costPrice ? 'border-red-500' : 'border-gray-300'
                                }`}
                            placeholder="0.00"
                        />
                        {errors.costPrice && <p className="text-red-500 text-sm mt-1">{errors.costPrice}</p>}
                    </div>

                    {/* Selling Price */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <DollarSign className="w-4 h-4 inline mr-2" />
                            Selling Price *
                        </label>
                        <input
                            type="number"
                            name="sellingPrice"
                            value={formData.sellingPrice}
                            onChange={handleInputChange}
                            min="0"
                            step="0.01"
                            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.sellingPrice ? 'border-red-500' : 'border-gray-300'
                                }`}
                            placeholder="0.00"
                        />
                        {errors.sellingPrice && <p className="text-red-500 text-sm mt-1">{errors.sellingPrice}</p>}

                        {/* Profit Margin Display */}
                        {formData.costPrice && formData.sellingPrice && (
                            <div className="mt-2 text-sm">
                                <span className="text-gray-600">Profit Margin: </span>
                                <span className={`font-semibold ${parseFloat(calculateMargin()) > 0 ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                    {calculateMargin()}%
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Description */}
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <FileText className="w-4 h-4 inline mr-2" />
                            Description
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            rows="3"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Optional product description"
                        />
                    </div>

                    {/* Active Status */}
                    <div className="md:col-span-2">
                        <label className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                name="isActive"
                                checked={formData.isActive}
                                onChange={handleInputChange}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm font-semibold text-gray-700">Active Product</span>
                        </label>
                        <p className="text-xs text-gray-500 mt-1">
                            Inactive products won't appear in sales and inventory lists
                        </p>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="flex gap-4 pt-6 border-t border-gray-200">
                    <button
                        type="submit"
                        disabled={loading}
                        onClick={handleSubmit}
                        className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
                    >
                        {loading ? 'Creating Product...' : 'Create Product'}
                    </button>

                    <button
                        type="button"
                        onClick={() => {
                            setFormData({
                                name: '',
                                sku: '',
                                description: '',
                                supplier: '',
                                unitOfMeasure: 'pieces',
                                costPrice: '',
                                sellingPrice: '',
                                currentStock: '0',
                                isActive: true
                            });
                            setErrors({});
                            setMessage({ type: '', text: '' });
                        }}
                        className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                    >
                        Reset Form
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateProductForm;